import {SlotSymbol} from 'com/gongxigames/math/core/game/engine/SlotSymbol';
import {Line} from 'com/gongxigames/math/core/game/Line';
import {ReelSet} from 'com/gongxigames/math/core/game/ReelSet';
import {Spin} from 'com/gongxigames/math/core/game/Spin';
import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {ResponseFilter} from 'com/gongxigames/math/core/client/ResponseFilter';
import {Session} from 'com/gongxigames/math/core/game/session/Session';
import {Bonus} from 'com/gongxigames/math/core/game/engine/bonus/Bonus';
import {FreeGamesBonus} from 'com/gongxigames/math/core/game/engine/bonus/FreeGamesBonus';
import {Pay} from 'com/gongxigames/math/core/game/Pay';
import {TumbleSpin, PayEntries} from '@gongxigames/framework/slotV2/types/LogicData';
import {PromotionQualifier} from '@gongxigames/framework/promotions';

export interface LogicConstructor {
    new (): ILogic;
}

export type RoundInfo = {
    win: number;
    totalWin: number;
    isBigWin: boolean;
};

export type BetInfo = {
    betIndex: number;
    bet: number;
    totalBet: number;
};

export interface ILogic {
    spinData: Spin;
    reelPositions: number[];
    nextAction: Action;
    hyperSpin: number[];
    betId: number;
    previousAction: Action;
    previousSpinRoundWin: number;

    initialize(): Promise<void>;

    getBonus<T extends Bonus>(bonusID: number): T;

    getUpgradeIndex(): number;

    getFirecatcherPrizes(): number[];

    getFirecatcherPrizeCount(): number;

    getFirecatcherRespinCount(): number;

    updateSessionBet(): void;

    isWaysGame(): boolean;

    isLinesGame(): boolean;

    // TODO use this function everywhere across the games
    isBigWin(): boolean;

    isBigTotalWin(): boolean;

    isSpecialWinConfigBuyFeature(): boolean;

    // TODO use this function everywhere across the games where need exactly limit
    getBigWinLimit(): number;

    nextActionIsSpin(): boolean;

    getMultiPickValue(): number;

    getMathVersion(): number;

    removeFromArray(src: number[], toRemove: number[]): void;

    getSlotSymbol(symbol: number): SlotSymbol;

    getNotPickedValues(): number[][] | null;

    getPickedValues(): number[][] | null;

    getResultsOfHiddenWins(): number[][];

    getJackpotPayments(): number[];

    isRetriggerWon(): boolean;

    isFreeSpinsWon(): boolean;

    getRetriggerCount(): number;

    setFilter(filter: ResponseFilter<Session>): void;

    setFilterForStopPositions(randomValues: number[] | null): void;

    getPickId(): number;

    getPicks(): number[];

    getPickGameWin(): number;

    getBets(): number[];

    getDefaultBet(): number;

    getFreeGamesCount(): number;

    getLines(): Line[] | null;

    getFreeGamesTotalCount(): number;

    getReelSet(): ReelSet;

    getReelsetByID(reelsetID: number): ReelSet;

    getCurrentReelsSymbols(): number[][];

    getNextReelSet(): ReelSet;

    getMainGameReelSet(): ReelSet;

    getWin(): number;

    getTotalWin(): number;

    getSessionBet(): number;

    getBalance(): number;

    getSlotDimension(): number[];

    getBet(): number;

    getWager(): number;

    getTotalBet(): number;

    getTotalSessionBet(): number;

    getBetFactor(symbolId: string | number): number;

    getSymbolIds(): number[][];

    getTumbleSpin(): TumbleSpin;

    isAnyCollapseOnSpin(): boolean;

    getPaysByStep(collapseStepID: number): Pay[]

    getByStepSymbolIds(collapseStepID: number): number[][];

    getByStepDropAmount(collapseStepID: number): number[];

    getByStepReelPositions(collapseStepID: number): number[];

    getByStepBeforeDropSymbolIds(collapseStepID: number): number[][];

    getLastStepSymbolIds(): number[][];

    getAllPays(): PayEntries;

    isAnyScatterPayOnTumbleSpin(): boolean;

    iterateTumbleSpinPays(callBack: (pays: Pay[]) => void, spin?: TumbleSpin): void

    spin(cb: Function): void;

    freespin(cb: Function): void;

    processSpin(): void;

    processFreeSpin(): void;

    pick(cb: Function, pickId: number): void;

    select(cb: Function, pickId: number): void;

    respin(cb: Function): void;

    minispin(cb: Function): void;

    buy(cb: Function): void;

    buyFeature(cb: Function): void;

    isAvailableNextAction(): boolean;

    getBuyFeatureMultiplier(): number;

    getBuyFeaturePrice(): number;

    getPromotionPrice(qualifier: PromotionQualifier, bet: number): number;

    getBuyFeatureOrUsualBonus(bonusId: number): number;

    canBuyFeature(): boolean;

    shouldHideBuyFeature(): boolean;

    shouldShowBuyFeatureDescription(): boolean;

    progressiveValues(cb: Function): void;

    getActionNumber(): number;

    getSession(totalActions: number): number[];

    getSessionFromLocalStorage(): number[] | undefined;

    getSessionForFreeSpinsWinningRound(): Spin;

    getSessionForFirecatcherWinningRound(): Spin;

    getSessionForRespinWinningRound(): Spin;

    getSessionForMiniSpinWinningRound(): Spin;

    getPreviousRoundSession(): Spin;

    getRestoredFreeGamesTotalCount(): number;

    isBonusOnFreeGames(bonusId?: number): boolean;

    getFreeGamesIndex(): number;

    getBonusWin(bonusId: number): number;

    canSpin(bet: number): boolean;

    getBaseWinFromTriggeringRound(currentBonusId: number): number;

    getBonusPrice(): number;

    getRealRubyCurrency(): number;

    getRubyCoinsToSpend(): number;

    getRubyBalance(): number;

    getLcdStake(): number;

    isBonusCanBeBought(): boolean;

    isCoinFlightFeature(symbolId: number): boolean;

    isLastFreeSpin(): boolean;

    getFreeGamesBonus(): FreeGamesBonus;

    getCurrentReelsStrips(): number[][];

    /**
     * Returns array in which length is equal to number of reels and every
     * array element is a number of symbols in indexed reel.
     */
    getBaseGameReelsMatrixSize(): number[];

    /**
     * Returns array with all used weighted positions in math
     * it help to create identical spin result by using ReelSetMatrix
     */
    getFinalSpinStopPositions(): number[];

    getRoundInfo(): RoundInfo;

    getBetInfo(): BetInfo;
}
